import type { FeatureEnum } from '../CRMEnums';
import type { Property, TransactionType } from './Property';

export const ORDER_BY = {
	ASC: 'asc',
	DESC: 'desc'
} as const;

export type OrderBy = (typeof ORDER_BY)[keyof typeof ORDER_BY];

/** Ordenação suportada pela API de Shorts (featured-videos). */
export const SHORTS_SORT_BY = {
	ASKING_PRICE: 'asking_price',
	SUCCESS_PROBABILITY: 'success_probability'
} as const;

export type ShortsSortBy = (typeof SHORTS_SORT_BY)[keyof typeof SHORTS_SORT_BY];

export type SortBy =
	| 'images_count'
	| 'asking_price'
	| 'portal_home_select'
	| 'default_ordenation'
	| 'distance'
	| 'wishlist_recent'
	| 'wishlist_oldest';

export interface PaginatedPropertyFilters {
	transactionType?: Exclude<TransactionType, 'rentOrSell'>;
	orderBy?: OrderBy;
	sortBy?: SortBy;
	page: number;
	perPage: number;
	searchTerm?: string;
	isExclusive?: boolean;
	features?: FeatureEnum[];
	propertyTypes?: Property['propertyType']['identifier'][];
	minArea?: number;
	maxArea?: number;
	minAskingPrice?: number;
	maxAskingPrice?: number;
	minRentPrice?: number;
	maxRentPrice?: number;
	minSquareMeterPrice?: number;
	maxSquareMeterPrice?: number;
	minCondoFee?: number;
	maxCondoFee?: number;
	minTax?: number;
	maxTax?: number;
	suites?: number;
	bedrooms?: number;
	bathrooms?: number;
	parkingSpots?: number;
	commercialIds?: string[];
	regions?: string[];
	cities?: string[];
	states?: string[];
	macroregions?: string[];
	countPluscodes?: string[];
	pluscodesGroup?: string[];
	onlyPluscodes?: boolean;
	isHeroSection?: boolean;
	locationSlug?: string;
	addressSlug?: string;
	similarTo?: string;
	/**
	 * Whether to filter the results to only include properties that are chosen for you.
	 * The chosen for you rule searches for properties that are curated if minAskingPrice and regions are not provided.
	 * Otherwise, it searches for properties with an minAskingPrice of 15M and on premium regions.
	 */
	isChosenForYou?: boolean;
	/**
	 * Array of property identifiers to exclude from the results.
	 */
	excludeProperties?: string[];
	/**
	 * The id of the curated list to filter the properties.
	 */
	curatedListId?: string;
	/**
	 * Show duplicated properties.
	 */
	showHideProperties?: boolean;
	/**
	 * Filter to only include properties that have videos.
	 */
	hasVideos?: boolean;
	/**
	 * The latitude of the point of interest or address slug, used for filtering lat/long coordinates.
	 * If provided, sort by distance will be applied.
	 * Also if provided, poi_distance_time will be calculated.
	 */
	poiLocationLat?: number;
	/**
	 * The longitude of the point of interest or address slug, used for filtering lat/long coordinates.
	 * If provieded, sort by distance will be applied.
	 * Also if provided, poi_distance_time will be calculated.
	 */
	poiLocationLng?: number;
	/**
	 * Map bounds in format 'west,south,east,north'
	 */
	bounds?: string;

	/**
	 * Map zoom level
	 */
	zoom?: number;

	/**
	 * Whether to include H3 cluster IDs in the response
	 */
	includeH3Clusters?: boolean;

	/**
	 * H3 cluster IDs to filter the properties
	 */
	h3Ids?: string[];

	/**
	 * Filter results to properties that belong to a specific wishlist.
	 */
	wishlistId?: string;

	/**
	 * Mark properties that are in the given user's wishlists.
	 * When provided, the API may return a flag per property (isInUserWishlists).
	 */
	markUserWishlistsId?: string;
	excludeDuplicationGroup?: string;
	/**
	 * When true, excludes properties that have featured videos (shorts).
	 */
	excludeFeaturedVideos?: boolean;
}

export type PropertyFilters = Omit<
	PaginatedPropertyFilters,
	'page' | 'perPage'
>;

export interface ShortsFilters {
	excludeProperties?: string[];
	markUserWishlistsId?: string;
	page?: number;
	perPage?: number;
	orderBy?: OrderBy;
	sortBy?: ShortsSortBy;
	cities?: string[];
	states?: string[];
	macroregions?: string[];
	regions?: string[];
	propertyTypes?: string[];
	features?: string[];
	minAskingPrice?: number;
	maxAskingPrice?: number;
	minArea?: number;
	maxArea?: number;
	bedrooms?: number;
	suites?: number;
	bathrooms?: number;
	parkingSpots?: number;
	displayInHomeShorts?: boolean;
	referenceProperty?: string;
	poiLocationLat?: number;
	poiLocationLng?: number;
	onlyUnwatched?: boolean;
	/**
	 * Parâmetro da API `featured-videos`: incluir imóvel só com imagem a cada N resultados.
	 */
	includeImagesEvery?: number;
}

/** Valor usado nas rotas `/shorts` (listagem e reel) ao chamar featured-videos. */
export const SHORTS_FEATURED_VIDEOS_INCLUDE_IMAGES_EVERY = 6;
